// src/contexts/SubscriptionContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext.js';
import monthlyWorkouts from '../data/workouts/monthly.js';
import { getError } from '../utils/errorMessages.js';
import { 
  getSubscription, 
  upgradeSubscription as apiUpgradeSubscription, 
  downgradeToBasic as apiDowngradeToBasic,
  downgradeToPremium as apiDowngradeToPremium,
  reactivateSubscription as apiReactivateSubscription,
  cancelSubscription as apiCancelSubscription,
  cancelSubscriptionImmediately as apiCancelSubscriptionImmediately,
  createCheckoutSession as apiCreateCheckoutSession,
  signupBasic as apiSignupBasic,
  calculateProration as apiCalculateProration,
  immediateDowngradeToBasic as apiImmediateDowngradeToBasic
} from '../services/api.js';

const PAID_TIERS = ['premium', 'elite'];

const hasActivePaidStripeSubscription = (sub) => {
  if (!sub) return false;
  if (sub.isFree) return false;
  if (sub.hasSubscription === false) return false;
  const tier = String(sub.tier || '').toLowerCase();
  if (!PAID_TIERS.includes(tier)) return false;
  const stripeSubscriptionId = sub.id || sub.stripeSubscriptionId;
  if (!stripeSubscriptionId) return false;
  const status = String(sub.status || 'active').toLowerCase();
  if (['canceled', 'cancelled', 'incomplete_expired', 'unpaid'].includes(status)) {
    return false;
  }
  return true;
};

const isMissingStripeSubscriptionError = (err) => {
  const message = (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    ''
  ).toLowerCase();
  return message.includes('no active subscription');
};

const startPaidCheckout = async (tier, interval = 'month') => {
  console.log('No active paid Stripe subscription; creating checkout for target tier:', tier);
  const data = await apiCreateCheckoutSession({ tier, interval });
  if (!data?.sessionId && !data?.url) {
    throw new Error('Checkout could not be started. Please try again or contact support.');
  }
  return {
    success: true,
    sessionId: data.sessionId,
    url: data.url,
    message: 'Redirecting to payment...'
  };
};

// Create context
const SubscriptionContext = createContext();

// Create provider component
export const SubscriptionProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const MIN_REFRESH_INTERVAL = 3000; // 3 seconds between refreshes

  // Fetch subscription data with improved logging, caching, and force refresh option
  const fetchSubscription = useCallback(async (forceRefresh = false) => {
    // Get current time to prevent excessive API calls
    const now = new Date();
    const minTimeBetweenFetches = 2000; // Increase to 2 seconds minimum between fetches
    
    console.log('Fetching subscription:', {
      forceRefresh,
      lastFetchTime,
      timeSinceLastFetch: lastFetchTime ? now - lastFetchTime : 'never',
      hasSubscription: !!subscription,
      userId: user?._id
    });
    
    // If we fetched recently and this isn't the first fetch, and not forcing refresh, don't fetch again
    if (
      !forceRefresh &&
      lastFetchTime && 
      now - lastFetchTime < minTimeBetweenFetches && 
      subscription !== null
    ) {
      console.log('Using cached subscription data');
      return subscription;
    }
    
    if (!user?._id) {
      console.log('No user ID, clearing subscription');
      setSubscription(null);
      setLoading(false);
      return null;
    }

    // Prevent multiple simultaneous requests
    if (loading) {
      console.log('Already loading subscription, skipping request');
      return subscription;
    }

    // Set loading state before the try block
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching subscription data from API');
      const data = await getSubscription();
      console.log('API returned subscription data:', data);
      
      if (!data) {
        console.log('No subscription data received from API');
        setError('Failed to load subscription information');
        setSubscription(null);
        return null;
      }
      
      // Ensure we have the correct tier information
      if (data.tier) {
        console.log('Processing subscription data:', data);
        const subscriptionData = {
          ...data,
          hasSubscription: data.hasSubscription !== undefined ? data.hasSubscription : (data.tier !== 'basic' || data.id !== undefined)
        };
        
        // Only update subscription if data has actually changed to prevent unnecessary re-renders
        setSubscription(prevSubscription => {
          if (JSON.stringify(prevSubscription) === JSON.stringify(subscriptionData)) {
            console.log('Subscription data unchanged, skipping update');
            return prevSubscription;
          }
          console.log('Updating subscription data');
          return subscriptionData;
        });
        
        setLastFetchTime(new Date());
        return subscriptionData;
      } else {
        console.log('Invalid subscription data received:', data);
        setError('Invalid subscription data received');
        setSubscription(null);
        return null;
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      // Check if it's an authentication error
      if (err.response && err.response.status === 401) {
        setSubscription(null);
      }
      
      setError('Failed to load subscription information');
      return null;
    } finally {
      // Always clear loading state in finally block
      console.log('Clearing loading state');
      setLoading(false);
    }
  }, [user?._id, lastFetchTime]);

  // Add this improved forceRefreshSubscription function with debouncing
  const forceRefreshSubscription = async (bypassThrottle = false) => {
    const now = Date.now();
    if (!bypassThrottle && now - lastRequestTime < MIN_REFRESH_INTERVAL) {
      console.log('Skipping refresh due to throttle');
      return subscription;
    }
    
    setLastRequestTime(now);
    
    // Add a small delay to prevent rapid successive updates
    if (!bypassThrottle) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    try {
      const result = await fetchSubscription(true);
      return result;
    } catch (error) {
      console.error('Error in forceRefreshSubscription:', error);
      return null;
    }
  };

  // Debug subscription state changes (only log significant changes)
  useEffect(() => {
    if (subscription?.tier && !loading) {
      console.log('Subscription state changed:', {
        tier: subscription.tier,
        hasSubscription: subscription.hasSubscription,
        status: subscription.status
      });
    }
  }, [subscription?.tier, subscription?.hasSubscription, subscription?.status, loading]);

  // Fetch subscription when user changes
  useEffect(() => {
    let isMounted = true;
    
    if (user?._id) {
      console.log('User changed, fetching subscription for user:', {
        userId: user._id,
        userEmail: user.email,
        userSubscription: user.subscription
      });
      // Clear cache to ensure fresh data is fetched
      setLastFetchTime(null);
      // Don't clear subscription data immediately to prevent flashing
      // setSubscription(null); // Removed to prevent flashing
      setLoading(true); // Set loading state
      // Force refresh when user changes to ensure fresh data
      fetchSubscription(true).then((result) => {
        if (!isMounted) {
          console.log('Component unmounted, skipping subscription update');
        } else {
          console.log('Subscription updated after user change:', {
            result,
            userSubscription: user.subscription,
            fetchedSubscription: result
          });
        }
      });
    } else {
      if (isMounted) {
        console.log('No user, clearing subscription data');
        setSubscription(null);
        setLoading(false);
        setLastFetchTime(null);
      }
    }
    
    return () => {
      isMounted = false;
    };
  }, [user?._id]); // Remove fetchSubscription from dependencies

  // Check if user has access to a specific tier
  const hasAccess = (requiredTier) => {
    // If still loading, return true to prevent premature redirects
    if (loading || authLoading) {
      return true;
    }

    // If not authenticated, deny access to all tiers
    if (!user) {
      return false;
    }

    // If no subscription data yet, only allow basic access
    if (!subscription) {
      return requiredTier === 'basic';
    }

    // Define tier levels
    const tierLevels = {
      'basic': 0,
      'premium': 1,
      'elite': 2
    };

    // Get user's current tier level
    const userTierLevel = tierLevels[subscription.tier] || 0;
    const requiredTierLevel = tierLevels[requiredTier] || 0;

    // For basic tier, check if user has any subscription
    if (requiredTier === 'basic') {
      return true; // Everyone has access to basic
    }

    // For premium and elite, check subscription status
    if (requiredTier === 'premium' || requiredTier === 'elite') {
      // Check if user's tier level meets or exceeds required level
      const hasRequiredTier = userTierLevel >= requiredTierLevel;
      
      // For elite tier users, they should have access to their tier regardless of hasSubscription status
      // (they might be on a free elite tier or have special access)
      if (subscription.tier === 'elite' && requiredTier === 'elite') {
        return true;
      }
      
      // For premium tier, check if user has an active subscription or is elite
      if (requiredTier === 'premium') {
        if (subscription.tier === 'elite') {
          return true;
        }
        if (subscription.tier === 'premium') {
          return true; // Premium tier users always have access to premium content
        }
        if (!subscription.hasSubscription) {
          return false;
        }
      }
      
      return hasRequiredTier;
    }

    return false; // Default to no access
  };
  
  // Calculate proration for changing subscription
  const calculateProration = async (newTier, interval = 'month') => {
    try {
      setLoading(true);
      
      console.log('Calculate proration - Current subscription:', {
        tier: subscription?.tier,
        id: subscription?.id,
        hasSubscription: subscription?.hasSubscription,
        status: subscription?.status
      });
      
      // For free tier, no proration needed
      if (subscription?.tier === 'basic' && !subscription?.id) {
        return {
          success: true,
          prorationDetails: {
            immediateCharge: 0,
            nextBillingAmount: 0,
            currency: 'usd'
          }
        };
      }
      
      // Check if user has a valid subscription for proration calculation
      if (!subscription?.hasSubscription || subscription?.tier === 'basic') {
        throw new Error('No active subscription found to calculate proration');
      }
      
      // Special case: Downgrading to Basic - Skip proration calculation
      if (newTier === 'basic') {
        console.log('Special case: Downgrade to Basic - skipping proration calculation');
        return {
          success: true,
          prorationDetails: {
            immediateCharge: 0, // No immediate charge for downgrades
            nextBillingAmount: 0, // Basic is free
            currency: 'usd',
            isDowngrade: true
          }
        };
      }
      
      // Special case: Downgrading from Elite to Premium - Skip proration calculation
      if (subscription?.tier === 'elite' && newTier === 'premium') {
        console.log('Special case: Downgrade from Elite to Premium - skipping proration calculation');
        return {
          success: true,
          prorationDetails: {
            immediateCharge: 0, // No immediate charge for downgrades
            nextBillingAmount: 9.99, // Hardcoded premium price - consider making dynamic
            currency: 'usd',
            isDowngrade: true
          }
        };
      }
      
      // Special case: Upgrading from Premium to Elite - Skip proration calculation
      if (subscription?.tier === 'premium' && newTier === 'elite') {
        console.log('Special case: Upgrade from Premium to Elite - skipping proration calculation');
        return {
          success: true,
          prorationDetails: {
            immediateCharge: 10.00, // Difference between premium and elite
            nextBillingAmount: 19.99, // Elite price
            currency: 'usd',
            isUpgrade: true
          }
        };
      }
      
      // Special case: Reactivation - Skip proration calculation
      if (subscription?.cancelAtPeriodEnd) {
        console.log('Reactivation case - skipping proration calculation');
        return {
          success: true,
          prorationDetails: {
            immediateCharge: 0,
            nextBillingAmount: subscription.tier === 'premium' ? 9.99 : 19.99,
            currency: 'usd',
            isReactivation: true
          }
        };
      }
      
      // Get proration details from API
      console.log('Calculating proration for tier change:', { fromTier: subscription?.tier, toTier: newTier, interval });
      const data = await apiCalculateProration({ tier: newTier, interval });
      
      return {
        success: true,
        prorationDetails: data
      };
    } catch (err) {
      console.error('Error calculating proration:', err);
      setError(err.response?.data?.error || 'Failed to calculate proration');
      
      return {
        success: false,
        error: err.response?.data?.error || 'Failed to calculate proration'
      };
    } finally {
      setLoading(false);
    }
  };

  // Upgrade subscription: Checkout for first paid plan (premium OR elite),
  // in-place Stripe change only when a paid subscription already exists.
  const upgradeSubscription = async (newTier, options = {}) => {
    try {
      const { interval = 'month', prorationBehavior = 'create_prorations' } = options;
      setLoading(true);

      const targetTier = String(newTier || '').toLowerCase();
      if (!PAID_TIERS.includes(targetTier)) {
        return {
          success: false,
          error: 'Please choose Premium or Elite to continue.'
        };
      }

      // Free/basic (or missing sub data) has no Stripe subscription to change.
      // Use the same Checkout session path as first-time subscribe, with the
      // chosen target tier so Elite gets the Elite price — not Premium-only.
      if (!hasActivePaidStripeSubscription(subscription)) {
        return await startPaidCheckout(targetTier, interval);
      }

      console.log('Upgrading existing paid subscription to:', targetTier);
      try {
        const data = await apiUpgradeSubscription({
          tier: targetTier,
          interval,
          prorationBehavior
        });

        await forceRefreshSubscription(true);
        return { success: true, message: data.message };
      } catch (upgradeErr) {
        if (isMissingStripeSubscriptionError(upgradeErr)) {
          console.log(
            'upgrade-subscription found no Stripe sub; falling back to checkout for:',
            targetTier
          );
          return await startPaidCheckout(targetTier, interval);
        }
        throw upgradeErr;
      }
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      console.error('Error response data:', err.response?.data);

      const errorMessage = getError(
        err,
        'Unable to start checkout. Please try again or contact support.'
      );

      setError(errorMessage);

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  // Downgrade to basic (free) tier at period end
  const downgradeToBasic = async () => {
    try {
      setLoading(true);
      const data = await apiDowngradeToBasic();
      await forceRefreshSubscription(true); // Use force refresh here
      return { 
        success: true, 
        message: data.message,
        effectiveDate: data.effectiveDate
      };
    } catch (err) {
      console.error('Error downgrading to basic:', err);
      setError(err.response?.data?.error || 'Failed to downgrade subscription');
      
      return { 
        success: false, 
        error: err.response?.data?.error || 'Failed to downgrade subscription' 
      };
    } finally {
      setLoading(false);
    }
  };
  
  // Downgrade to basic (free) tier immediately
  const immediateDowngradeToBasic = async () => {
    try {
      setLoading(true);
      const data = await apiImmediateDowngradeToBasic();
      await forceRefreshSubscription(true); // Use force refresh here
      return { 
        success: true, 
        message: data.message,
        effectiveDate: data.effectiveDate
      };
    } catch (err) {
      console.error('Error immediately downgrading to basic:', err);
      setError(err.response?.data?.error || 'Failed to downgrade subscription');
      
      return { 
        success: false, 
        error: err.response?.data?.error || 'Failed to downgrade subscription immediately' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Downgrade from Elite to Premium tier
  const downgradeToPremium = async (interval = 'month') => {
    try {
      setLoading(true);
      const data = await apiDowngradeToPremium(interval);
      await forceRefreshSubscription(true); // Force refresh immediately
      return { 
        success: true, 
        message: data.message || 'Successfully downgraded to Premium tier.',
        subscription: data.subscription
      };
    } catch (err) {
      console.error('Error downgrading to premium:', err);
      setError(err.response?.data?.error || 'Failed to downgrade subscription');
      
      return { 
        success: false, 
        error: err.response?.data?.error || 'Failed to downgrade to Premium tier' 
      };
    } finally {
      setLoading(false);
    }
  };
  
  // Reactivate subscription
  const reactivateSubscription = async () => {
    try {
      setLoading(true);
      
      // Check if subscription exists and is set to cancel
      if (!subscription) {
        throw new Error('No subscription found to reactivate');
      }
      
      // Better verification - check if it's actually canceling
      if (!subscription.cancelAtPeriodEnd) {
        throw new Error('Subscription is not scheduled for cancellation');
      }
      
      console.log('Reactivating subscription', subscription.id);
      const data = await apiReactivateSubscription();
      
      await forceRefreshSubscription(true);
      return {
        success: true,
        message: data.message || 'Your subscription has been reactivated successfully.'
      };
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      setError(err.response?.data?.error || 'Failed to reactivate subscription');
      
      return {
        success: false,
        error: err.response?.data?.error || 'Failed to reactivate subscription'
      };
    } finally {
      setLoading(false);
    }
  };

  // Cancel subscription at period end
  const cancelSubscription = async () => {
    try {
      setLoading(true);
      const data = await apiCancelSubscription();
      await forceRefreshSubscription(true); // Use force refresh here
      return { 
        success: true, 
        message: data.message,
        effectiveDate: data.effectiveDate
      };
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(err.response?.data?.error || 'Failed to cancel subscription');
      
      return { 
        success: false, 
        error: err.response?.data?.error || 'Failed to cancel subscription' 
      };
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel subscription immediately
  const cancelSubscriptionImmediately = async () => {
    try {
      setLoading(true);
      const data = await apiCancelSubscriptionImmediately();
      await forceRefreshSubscription(true); // Use force refresh here
      return { 
        success: true, 
        message: data.message,
        effectiveDate: data.effectiveDate
      };
    } catch (err) {
      console.error('Error canceling subscription immediately:', err);
      setError(err.response?.data?.error || 'Failed to cancel subscription immediately');
      
      return { 
        success: false, 
        error: err.response?.data?.error || 'Failed to cancel subscription immediately' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Create checkout session for new subscription
  const createCheckoutSession = async (tier, interval = 'month') => {
    try {
      setLoading(true);
      
      // For basic tier, use a different endpoint
      if (tier.toLowerCase() === 'basic') {
        const data = await apiSignupBasic();
        await forceRefreshSubscription(true); // Force refresh after signup
        return { 
          success: true, 
          isFree: true, 
          message: data.message 
        };
      }
      
      const data = await apiCreateCheckoutSession({ tier, interval });
      
      // Check if this is a free tier redirect
      if (data.isFree && data.redirectTo) {
        const response = await apiSignupBasic();
        await forceRefreshSubscription(true); // Force refresh after signup
        return { 
          success: true, 
          isFree: true, 
          message: response.message 
        };
      }
      
      return { 
        success: true, 
        isFree: false, 
        sessionId: data.sessionId,
        url: data.url // Add URL if available
      };
    } catch (err) {
      console.error('Error creating checkout session:', err);
      const errorMessage = getError(err, 'Unable to start checkout. Please try again or contact support.');
      setError(errorMessage);
      
      return { 
        success: false, 
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  // Format subscription tier display name
  const formatTierName = (tier) => {
    if (!tier) return 'Free';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };
  
  // Format dates for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Get tiers that are valid upgrade options
  const getValidUpgradeTiers = () => {
    const currentTier = subscription?.tier || 'basic';
    const tierLevels = {
      'basic': 0,
      'premium': 1,
      'elite': 2
    };
    
    const currentLevel = tierLevels[currentTier] || 0;
    const upgradeTiers = [];
    
    if (currentLevel < 1) {
      upgradeTiers.push('premium');
    }
    
    if (currentLevel < 2) {
      upgradeTiers.push('elite');
    }
    
    return upgradeTiers;
  };
  
  // Get tiers that are valid downgrade options
  const getValidDowngradeTiers = () => {
    const currentTier = subscription?.tier || 'basic';
    const tierLevels = {
      'basic': 0,
      'premium': 1,
      'elite': 2
    };
    
    const currentLevel = tierLevels[currentTier] || 0;
    const downgradeTiers = [];
    
    // Always add basic as a downgrade option for paid tiers
    if (currentLevel > 0) {
      downgradeTiers.push('basic');
    }
    
    // Add premium as downgrade option for elite
    if (currentLevel > 1) {
      downgradeTiers.push('premium');
    }
    
    return downgradeTiers;
  };

  // Monthly Content Release System
  const getMonthlyContentAccess = () => {
    if (!subscription || (subscription.tier !== 'elite' && subscription.tier !== 'premium')) {
      return {
        month1: false,
        month2: false,
        month3: false,
        month4: false,
        month5: false,
        month6: false
      };
    }

    // Use paidMonths field only for admin users, otherwise fall back to payment history calculation
    let monthsOfAccess = 0;
    
    console.log('getMonthlyContentAccess - user:', user);
    console.log('getMonthlyContentAccess - subscription:', subscription);
    console.log('getMonthlyContentAccess - user.role:', user?.role);
    console.log('getMonthlyContentAccess - subscription.paidMonths:', subscription?.paidMonths);
    
    if (user?.role === 'admin') {
          const availableMonths = Object.keys(monthlyWorkouts).map(Number);
          const adminAccess = {
            month1: availableMonths.includes(1),
            month2: availableMonths.includes(2),
            month3: availableMonths.includes(3),
            month4: availableMonths.includes(4),
            month5: availableMonths.includes(5),
            month6: availableMonths.includes(6)
          };
          console.log('Admin user - returning full monthly content access:', adminAccess);
          return adminAccess;
        } else if (subscription.tier === 'premium' || subscription.tier === 'elite') {
          // Premium and Elite users get Month 1 immediately, Month 2+ based on actual payment history
          // Check if they have paidMonths field (from Stripe webhook updates)
          if (subscription.paidMonths && subscription.paidMonths > 0) {
            monthsOfAccess = subscription.paidMonths - 1; // Month 1 is always free, so subtract 1
            console.log(`${subscription.tier} user - paidMonths: ${subscription.paidMonths}, monthsOfAccess: ${monthsOfAccess}`);
          } else {
            // Fallback to payment history calculation
            const paymentHistory = subscription.paymentHistory || [];
            
            if (paymentHistory.length > 0) {
              // Sort payments by date (newest first)
              const sortedPayments = paymentHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
              
              // Count consecutive monthly payments
              let consecutiveMonths = 0;
              let lastPaymentDate = null;
              
              for (const payment of sortedPayments) {
                const paymentDate = new Date(payment.date);
                
                if (lastPaymentDate === null) {
                  // First payment
                  consecutiveMonths = 1;
                  lastPaymentDate = paymentDate;
                } else {
                  // Check if this payment is within 35 days of the last payment (allowing for some flexibility)
                  const daysDifference = Math.abs(paymentDate - lastPaymentDate) / (1000 * 60 * 60 * 24);
                  
                  if (daysDifference <= 35) {
                    consecutiveMonths++;
                    lastPaymentDate = paymentDate;
                  } else {
                    // Gap too large, reset count
                    break;
                  }
                }
              }
              
              monthsOfAccess = consecutiveMonths - 1; // Month 1 is always free, so subtract 1
              console.log(`${subscription.tier} user - payment history: ${consecutiveMonths} payments, monthsOfAccess: ${monthsOfAccess}`);
            } else {
              monthsOfAccess = 0; // No payment history, only Month 1 available
              console.log(`${subscription.tier} user - no payment history, only Month 1 available`);
            }
          }

          // Ensure users can see all published months even if paidMonths data hasn't been synced yet
          const totalAvailableMonths = Object.keys(monthlyWorkouts).length;
          const publishedMonthsBeyondFirst = Math.max(totalAvailableMonths - 1, 0);
          if (monthsOfAccess < publishedMonthsBeyondFirst) {
            console.log(`Extending access to published months (${publishedMonthsBeyondFirst}) for ${subscription.tier} user due to incomplete billing sync.`);
            monthsOfAccess = publishedMonthsBeyondFirst;
          }
        } else {
          // Basic users get no monthly content access
          monthsOfAccess = 0;
          console.log('Basic user - no monthly content access');
        }
    
    // Month 1 is always available for premium and elite users
    // Month 2+ requires payment history or paidMonths
    const monthExists = (monthNumber) => Boolean(monthlyWorkouts[monthNumber]);

    const result = {
      month1: monthExists(1),
      month2: monthExists(2) && monthsOfAccess >= 1,
      month3: monthExists(3) && monthsOfAccess >= 2,
      month4: monthExists(4) && monthsOfAccess >= 3,
      month5: monthExists(5) && monthsOfAccess >= 4,
      month6: monthExists(6) && monthsOfAccess >= 5
    };
    
    console.log('getMonthlyContentAccess result:', result, 'monthsOfAccess:', monthsOfAccess);
    return result;
  };

  // Get available monthly packages for current user
  const getAvailableMonthlyPackages = () => {
    console.log('getAvailableMonthlyPackages called');
    const contentAccess = getMonthlyContentAccess();
    console.log('getAvailableMonthlyPackages - contentAccess:', contentAccess);
    const packages = [];
    
    // Determine user's current tier
    const userTier = subscription?.tier || 'basic';
    const isElite = userTier === 'elite';

    const getMonthMeta = (monthNumber, fallbackName, fallbackDescription) => {
      const monthData = monthlyWorkouts?.[monthNumber];
      const name = monthData?.title || fallbackName;
      const description = monthData?.description
        ? monthData.description.split('\n')[0]
        : fallbackDescription;
      return { name, description };
    };

    if (contentAccess.month1) {
      const meta = getMonthMeta(1, 'The Classics', 'Foundation Elite - Professional guided basics with classic movements and proven techniques');
      packages.push({
        id: 'month1',
        name: meta.name,
        description: meta.description,
        available: true,
        month: 1,
        tier: isElite ? 'elite' : 'premium' // Elite users see elite tier, premium users see premium tier
      });
    }
    
    if (contentAccess.month2) {
      const meta = getMonthMeta(2, 'Fight for Your Fitness', 'Progressive Elite - Advanced movement patterns and strength building with motivational challenges');
      packages.push({
        id: 'month2',
        name: meta.name,
        description: meta.description,
        available: true,
        month: 2,
        tier: isElite ? 'elite' : 'premium' // Elite users see elite tier, premium users see premium tier
      });
    }
    
    if (contentAccess.month3) {
      const meta = getMonthMeta(3, 'Gratitude and Grit', 'Resilience-focused training that blends strength, conditioning, and mindful recovery.');
      packages.push({
        id: 'month3',
        name: meta.name,
        description: meta.description,
        available: true,
        month: 3,
        tier: isElite ? 'elite' : 'premium' // Elite users see elite tier, premium users see premium tier
      });
    }
    
    // Add locked packages for months not yet available
    if (!contentAccess.month2) {
      const meta = getMonthMeta(2, 'Fight for Your Fitness', 'Progressive Elite - Advanced movement patterns and strength building with motivational challenges');
      packages.push({
        id: 'month2',
        name: meta.name,
        description: meta.description,
        available: false,
        month: 2,
        lockedReason: 'Available on Next Subscription',
        tier: isElite ? 'elite' : 'premium'
      });
    }
    
    if (!contentAccess.month3) {
      const meta = getMonthMeta(3, 'Gratitude and Grit', 'Resilience-focused training that blends strength, conditioning, and mindful recovery.');
      packages.push({
        id: 'month3',
        name: meta.name,
        description: meta.description,
        available: false,
        month: 3,
        lockedReason: 'Coming Soon',
        tier: isElite ? 'elite' : 'premium'
      });
    }
    
    console.log('getAvailableMonthlyPackages - final packages:', packages);
    return packages;
  };

  // Check if user has paid for current billing period
  const hasPaidForCurrentPeriod = () => {
    if (!subscription?.currentPeriodStart || !subscription?.status) {
      return false;
    }
    
    // Check if subscription is active and paid
    const isActive = subscription.status === 'active' || subscription.status === 'trialing';
    const hasValidPayment = subscription.paymentStatus === 'paid' || subscription.paymentStatus === 'succeeded';
    
    return isActive && hasValidPayment;
  };

  // Content Version Management
  const getContentVersion = () => {
    if (!subscription || subscription.tier !== 'elite') {
      return { version: 'basic', availableMonths: 0 };
    }

    const contentAccess = getMonthlyContentAccess();
    const availableMonths = Object.values(contentAccess).filter(Boolean).length;
    
    return {
      version: `elite-v${availableMonths}`,
      availableMonths,
      contentAccess
    };
  };

  // Get next payment date for content release
  const getNextContentReleaseDate = () => {
    if (!subscription?.currentPeriodEnd) {
      return null;
    }
    
    const nextReleaseDate = new Date(subscription.currentPeriodEnd);
    return nextReleaseDate;
  };

  // Check if user is eligible for next monthly content
  const isEligibleForNextContent = () => {
    const contentVersion = getContentVersion();
    const nextReleaseDate = getNextContentReleaseDate();
    
    if (!nextReleaseDate) {
      return false;
    }
    
    const now = new Date();
    const isPaymentDue = now >= nextReleaseDate;
    const hasPaid = hasPaidForCurrentPeriod();
    
    return isPaymentDue && hasPaid;
  };

  // Subscription Resumption System
  const getSubscriptionResumePoint = () => {
    if (!subscription) {
      return { canResume: false, resumeFromMonth: 1, lastActiveMonth: 0 };
    }

    // Only allow resume when subscription is not currently active
    const resumeEligibleStatuses = ['canceled', 'paused', 'incomplete', 'incomplete_expired', 'past_due', 'unpaid'];
    const status = subscription.status || 'active';
    if (!resumeEligibleStatuses.includes(status)) {
      return { canResume: false, resumeFromMonth: 0, lastActiveMonth: 0 };
    }

    // Get the last month the user had access to before subscription ended
    const lastActiveDate = subscription.lastActiveDate || subscription.currentPeriodStart;
    const subscriptionEndDate = subscription.currentPeriodEnd;
    const now = new Date();
    
    // If somehow status indicates ended but end date is still in future, avoid showing resume
    if (now <= new Date(subscriptionEndDate || now)) {
      return { canResume: false, resumeFromMonth: 0, lastActiveMonth: 0 };
    }
    
    // Calculate how many months of content they had access to
    const paymentHistory = subscription.paymentHistory || [];
    let monthsOfAccess = 0;
    let previousTier = subscription.tier || 'premium';
    
    if (paymentHistory.length > 0) {
      const sortedPayments = paymentHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
      let consecutiveMonths = 0;
      let lastPaymentDate = null;
      
      // Get the tier from the most recent payment
      if (sortedPayments.length > 0) {
        previousTier = sortedPayments[0].tier || sortedPayments[0].planLevel || subscription.tier || 'premium';
      }
      
      for (const payment of sortedPayments) {
        const paymentDate = new Date(payment.date);
        
        if (lastPaymentDate === null) {
          consecutiveMonths = 1;
          lastPaymentDate = paymentDate;
        } else {
          const daysDifference = Math.abs(paymentDate - lastPaymentDate) / (1000 * 60 * 60 * 24);
          
          if (daysDifference <= 35) {
            consecutiveMonths++;
            lastPaymentDate = paymentDate;
          } else {
            break;
          }
        }
      }
      
      monthsOfAccess = consecutiveMonths;
    }
    
    // User can resume from the month they last had access to
    const resumeFromMonth = Math.max(1, monthsOfAccess);
    
    return {
      canResume: true,
      resumeFromMonth,
      lastActiveMonth: monthsOfAccess,
      lastActiveDate,
      subscriptionEndDate,
      previousTier
    };
  };

  // Resume subscription from where user left off
  const resumeSubscriptionFromPoint = async (resumeFromMonth) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://elderfit.onrender.com/api';
      
      // Use the user's current active subscription tier
      let userTier = subscription?.tier || subscription?.planLevel || 'premium';
      
      console.log('Resuming subscription with current tier:', userTier);
      console.log('Current subscription:', subscription);
      
      const response = await fetch(`${apiBaseUrl}/subscription/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          resumeFromMonth,
          tier: userTier
        })
      });

      if (response.ok) {
        const updatedSubscription = await response.json();
        setSubscription(updatedSubscription);
        return { success: true, subscription: updatedSubscription };
      } else {
        const error = await response.json();
        return { success: false, error: error.message || 'Failed to resume subscription' };
      }
    } catch (error) {
      console.error('Error resuming subscription:', error);
      // Get user-friendly error message
      return { success: false, error: getError(error, 'Unable to resume subscription. Please try again.') };
    }
  };

  // Get resumption options for user
  const getResumptionOptions = () => {
    const resumeInfo = getSubscriptionResumePoint();
    
    if (!resumeInfo.canResume) {
      return [];
    }
    
    const options = [];
    const currentTier = subscription?.tier || 'premium';
    const previousTier = resumeInfo.previousTier || currentTier;
    
    // Only show option to resume from where they left off
    // No "Start Fresh" option - user should resume from where they halted
    options.push({
      id: 'resume-from-point',
      title: `Resume from Month ${resumeInfo.resumeFromMonth}`,
      description: `Continue your ${currentTier} subscription from where you left off${previousTier !== currentTier ? ` (previously ${previousTier})` : ''}`,
      resumeFromMonth: resumeInfo.resumeFromMonth,
      type: 'resume',
      currentTier,
      previousTier
    });
    
    return options;
  };

  // Create context value
  const value = {
    subscription,
    loading,
    error,
    hasAccess,
    calculateProration,
    upgradeSubscription,
    downgradeToBasic,
    immediateDowngradeToBasic,
    downgradeToPremium,
    reactivateSubscription,
    cancelSubscription,
    cancelSubscriptionImmediately,
    createCheckoutSession,
    formatTierName,
    formatDate,
    getValidUpgradeTiers,
    getValidDowngradeTiers,
    refreshSubscription: fetchSubscription,
    forceRefreshSubscription,
    // Monthly Content Release System
    getMonthlyContentAccess,
    getAvailableMonthlyPackages,
    hasPaidForCurrentPeriod,
    getContentVersion,
    getNextContentReleaseDate,
    isEligibleForNextContent,
    // Subscription Resumption System
    getSubscriptionResumePoint,
    resumeSubscriptionFromPoint,
    getResumptionOptions,
    // Add userTier for easy access
    userTier: subscription?.tier || 'basic'
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Create custom hook for using the subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;